import { Button, Chip, Table } from "@mui/joy";
import React from "react";
import { Util } from "../Helpers/Util";
import { Elevator } from "../Interfaces/Elevator";
import InspectionReportDialog from "../Dialogs/InspectionReportDialog";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface InspectionReportsListingProps {
	buildingId: string;
}

export interface InspectionReport {
	inspection_reports_id: string;
	buildings_id: string;
	date_entered: Date;
	file_name: string;
	elevator: string;
	elevators_id: string;
	description: string;
}

// Unified row type merging manual reports and attempt-linked PDFs for display
interface ReportRow {
	key: string;
	description: string;
	date: Date | null;
	elevator: string;
	source: "manual" | "attempt";
	// For manual reports — open via server route
	inspection_reports_id?: string;
	// For attempt PDFs — open directly from blob URL
	blob_url?: string;
	// The underlying InspectionReport object for the Edit button (manual only)
	raw?: InspectionReport;
}

export default function InspectionReportsListing(props: InspectionReportsListingProps) {
	const [inspections, setInspections] = React.useState([] as Array<InspectionReport>);
	const [inspection, setInspection] = React.useState<InspectionReport>({} as InspectionReport);
	const [dialog, setDialog] = React.useState(false);
	const [elevators, setElevators] = React.useState<Array<Elevator>>([]);
	const [attemptPdfs, setAttemptPdfs] = React.useState<Array<any>>([]);

	React.useEffect(() => {
		getData();
		getElevators();
		getAttemptPdfs();
	}, [props.buildingId]);

	const getElevators = () => {
		fetch("Building/GetElevators", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify({ buildingsId: props.buildingId }),
		})
			.then((x) => x.json())
			.then((x) => setElevators(x));
	};

	const getData = () => {
		fetch("Inspection/GetInspections", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(props.buildingId),
		})
			.then((x) => x.json())
			.then((x) => setInspections(x as Array<InspectionReport>));
	};

	const getAttemptPdfs = () => {
		fetch("Inspection/GetInspectionAttemptPdfsForBuilding", {
			method: "Post",
			cache: "no-cache",
			headers: { "Content-Type": "application/json" },
			body: JSON.stringify(props.buildingId),
		})
			.then((x) => x.json())
			.then((x) => setAttemptPdfs(x));
	};

	const openInspection = (row: ReportRow) => {
		if (row.source === "manual" && row.inspection_reports_id) {
			window.open(document.location.origin + "/Inspection/" + row.inspection_reports_id, "_blank");
		} else if (row.source === "attempt" && row.blob_url) {
			window.open(row.blob_url, "_blank");
		}
	};

	// Merge manual reports and attempt PDFs into a single sorted list
	const attemptLabels = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth", "Seventh", "Eighth", "Ninth", "Tenth"];
	const rows: Array<ReportRow> = [
		...inspections.map((x) => ({
			key: x.inspection_reports_id,
			description: x.description,
			date: x.date_entered,
			elevator: x.elevator,
			source: "manual" as const,
			inspection_reports_id: x.inspection_reports_id,
			raw: x,
		})),
		...attemptPdfs.map((x) => ({
			key: x.inspection_attempt_pdf_id,
			description: `${attemptLabels[(x.attempt_number ?? 1) - 1] ?? x.attempt_number} Inspection — ${x.file_name}`,
			date: x.uploaded_date ?? null,
			elevator: x.elevator ?? "",
			source: "attempt" as const,
			blob_url: x.blob_url,
		})),
	].sort((a, b) => {
		// Sort newest first; nulls go to end
		if (!a.date) return 1;
		if (!b.date) return -1;
		return new Date(b.date).getTime() - new Date(a.date).getTime();
	});

	return (
		<LocalizationProvider dateAdapter={AdapterDateFns}>
			<InspectionReportDialog
				buildingId={props.buildingId}
				open={dialog}
				inspectionReport={inspection}
				close={() => {
					setDialog(false);
					getData();
					getAttemptPdfs();
				}}
				elevators={elevators}
			/>
			<Button
				sx={{ mt: 1 }}
				onClick={() => {
					setInspection({} as InspectionReport);
					setDialog(true);
				}}>
				Add Report
			</Button>
			<Table stripe="even" hoverRow={true} sx={{ cursor: "pointer" }}>
				<thead>
					<tr>
						<th>Inspection</th>
						<th>Date</th>
						<th>Elevator</th>
						<th>Source</th>
						<th>Edit</th>
					</tr>
				</thead>
				<tbody>
					{rows.map((x) => (
						<tr key={x.key} style={{ cursor: "pointer" }} onClick={() => openInspection(x)}>
							<td>{x.description}</td>
							<td>{x.date ? Util.Formatter(x.date, "D") : ""}</td>
							<td>{x.elevator}</td>
							<td>
								{x.source === "attempt" ? (
									<Chip size="sm" variant="soft" color="neutral">Tracking</Chip>
								) : (
									<Chip size="sm" variant="soft" color="neutral">Manual</Chip>
								)}
							</td>
							<td>
								{x.source === "manual" && x.raw && (
									<Button
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											setInspection(x.raw!);
											setDialog(true);
										}}>
										Edit
									</Button>
								)}
							</td>
						</tr>
					))}
				</tbody>
			</Table>
		</LocalizationProvider>
	);
}
